import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { EASE } from "@/lib/motion";
import { Reveal } from "@/components/Reveal";
import { SectionHeading, CornerTicks, btnGhost } from "@/components/kit";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const LAYERS = [
    { name: "USER", desc: "Humans and organizations define objectives and fund tasks." },
    { name: "TASK", desc: "Objectives become structured tasks with rewards, capability requirements and deadlines." },
    { name: "AGENT", desc: "Autonomous entities accept tasks they have the capability to perform." },
    { name: "ACTION", desc: "Work is executed in the physical world or on the network." },
    { name: "EVIDENCE", desc: "Execution produces evidence — sensor data, logs, images, signatures." },
    { name: "VERIFICATION", desc: "The evidence package is evaluated and scored by the verification engine." },
    { name: "ACTION PROOF", desc: "A verified action is written as a durable, inspectable proof." },
    { name: "REPUTATION", desc: "Every proof updates the agent's public track record." },
    { name: "USDC SETTLEMENT", desc: "Verified outcomes trigger USDC payment." },
    { name: "ARC", desc: "Arc Mainnet provides the USDC-native settlement environment." },
];

export const ProtocolArchitecture = () => {
    const [active, setActive] = useState(0);

    return (
        <section id="protocol" data-testid="protocol-architecture" className="section relative">
            <div className="shell">
                <Reveal>
                    <SectionHeading
                        index="04"
                        eyebrow="PROTOCOL ARCHITECTURE"
                        title={
                            <>
                                HOW <span className="text-[#1677FF]">ACTRA</span> WORKS
                            </>
                        }
                        description="Ten layers connect objectives to economic outcomes. Every layer is inspectable."
                    />
                </Reveal>

                <Reveal delay={0.1} className="mt-14">
                    <div className="grid gap-8 lg:grid-cols-[1.1fr_1fr] lg:items-stretch">
                        {/* Visual */}
                        <div className="relative overflow-hidden border border-white/[0.08] bg-[#040811]">
                            <img
                                src="/actra-ban.webp"
                                alt="ACTRA protocol — autonomous agents executing and verifying tasks"
                                className="block h-full min-h-[300px] w-full object-contain"
                                loading="lazy"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#040811] via-transparent to-[#040811]/30" />
                            <div className="absolute bottom-0 left-0 right-0 flex items-end justify-between gap-4 p-6">
                                <p className="label-tech">PROTOCOL VISUAL / ARC ENVIRONMENT</p>
                                <span className="font-mono text-[10px] tracking-[0.2em] text-[#31D8FF]">ACTRA / 5042</span>
                            </div>
                        </div>

                        {/* Interactive stack */}
                        <div className="relative border border-white/[0.08] bg-[#07111F]/70 p-6 md:p-8">
                            <CornerTicks />
                            <p className="label-tech mb-5">STACK — SELECT A LAYER</p>
                            <div className="grid grid-cols-1 gap-px sm:grid-cols-2">
                                {LAYERS.map((l, i) => (
                                    <button
                                        key={l.name}
                                        data-testid={`protocol-layer-${l.name.toLowerCase().replace(/\s+/g, "-")}`}
                                        onMouseEnter={() => setActive(i)}
                                        onClick={() => setActive(i)}
                                        aria-pressed={active === i}
                                        className={`flex items-center justify-between gap-3 border px-4 py-3.5 text-left font-mono text-[10px] font-semibold uppercase tracking-[0.18em] transition-all duration-300 ${
                                            active === i
                                                ? "border-[#1677FF] bg-[#1677FF]/15 text-ink"
                                                : "border-white/[0.07] bg-[#0A1627]/70 text-mute hover:text-ink"
                                        }`}
                                    >
                                        <span className="flex items-center gap-3">
                                            <span className={active === i ? "text-[#31D8FF]" : "text-mute/50"}>
                                                {String(i + 1).padStart(2, "0")}
                                            </span>
                                            {l.name}
                                        </span>
                                        <ArrowRight size={11} className={active === i ? "text-[#1677FF]" : "text-transparent"} />
                                    </button>
                                ))}
                            </div>
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={active}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    transition={{ duration: 0.3, ease: EASE }}
                                    className="mt-6 border-l-2 border-[#1677FF] bg-[#0A1627] p-5"
                                    data-testid="protocol-layer-detail"
                                >
                                    <p className="font-display text-lg font-bold uppercase tracking-tightest text-ink">
                                        {LAYERS[active].name}
                                    </p>
                                    <p className="mt-1.5 text-sm leading-relaxed text-mute">{LAYERS[active].desc}</p>
                                </motion.div>
                            </AnimatePresence>
                            <div className="mt-6 flex justify-end">
                                <Link to="/docs" data-testid="protocol-read-spec" className={`${btnGhost} px-5 py-3 text-[10px]`}>
                                    READ THE SPECIFICATION <ArrowRight size={12} />
                                </Link>
                            </div>
                        </div>
                    </div>
                </Reveal>
            </div>
        </section>
    );
};

export default ProtocolArchitecture;
